import React, { useState, useEffect } from 'react';
import { TikTokRecipe, TikTokIngredient } from '../../types/tiktok';
import { collection, getDocs, addDoc, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../lib/firebase';
import { Organization, OrganizationMember } from '../../types/index';
import { StandardIngredient } from '../../types/ingredients';
import { Save, X, Building2, Users, Trash2, Edit2, Check } from 'lucide-react';
import { generateRecipeSeoUrl } from '../../utils/seoUtils';
import { useAuthState } from 'react-firebase-hooks/auth';
import { mapToStandardUnit } from '../../utils/unitMapping';
import { getUnitLabel } from '../../utils/translationHelpers';
import { downloadAndUploadImage, isTikTokImageUrl } from '../../utils/imageUpload';

interface TikTokRecipeEditorProps {
  recipe: TikTokRecipe;
  onSuccess: () => void;
  onCancel: () => void;
}

export const TikTokRecipeEditor: React.FC<TikTokRecipeEditorProps> = ({
  recipe: initialRecipe,
  onSuccess,
  onCancel
}) => {
  const [user] = useAuthState(auth);
  const [recipe, setRecipe] = useState<TikTokRecipe>(initialRecipe);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [orgMembers, setOrgMembers] = useState<OrganizationMember[]>([]);
  const [isPublic, setIsPublic] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingIngredientId, setEditingIngredientId] = useState<string | null>(null);
  const [editAmount, setEditAmount] = useState<number>(0);
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrgId) {
      const selectedOrg = organizations.find(org => org.id === selectedOrgId);
      if (selectedOrg) {
        setOrgMembers(selectedOrg.members);
        if (selectedOrg.members.length > 0) {
          setSelectedUserId(selectedOrg.members[0].userId);
        } else if (user) {
          setSelectedUserId(user.uid);
        }
      }
    }
  }, [selectedOrgId, organizations, user]);

  const fetchOrganizations = async () => {
    try {
      const orgsQuery = collection(db, 'organizations');
      const snapshot = await getDocs(orgsQuery);
      const orgs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Organization));
      setOrganizations(orgs);
      
      if (orgs.length > 0) {
        setSelectedOrgId(orgs[0].id);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setError('Impossible de charger les organisations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveIngredient = (ingredientId: string) => {
    setRecipe(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter(ing => ing.id !== ingredientId)
    }));
  };

  const handleEditIngredient = (ingredientId: string, currentAmount: number) => {
    setEditingIngredientId(ingredientId);
    setEditAmount(currentAmount);
  };

  const handleSaveIngredientEdit = (ingredientId: string) => {
    setRecipe(prev => ({
      ...prev,
      ingredients: prev.ingredients.map(ing => 
        ing.id === ingredientId ? { ...ing, amount: editAmount } : ing
      )
    }));
    setEditingIngredientId(null);
  };

  const handleCancelEdit = () => {
    setEditingIngredientId(null);
  };

  const handleSave = async () => {
    if (!selectedOrgId) {
      alert('Veuillez sélectionner une organisation');
      return;
    }

    if (!selectedUserId) {
      alert('Veuillez sélectionner un créateur pour la recette');
      return;
    }

    if (!user) {
      setError('Utilisateur non authentifié');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Upload TikTok image to Firebase Storage if needed
      let finalImageUrl = recipe.image || '';
      if (recipe.image && isTikTokImageUrl(recipe.image)) {
        try {
          setIsUploadingImage(true);
          console.log('🔄 Uploading TikTok image to Firebase Storage...');
          // Use authenticated user's UID for Firebase Storage rules compliance
          // Storage rules require: request.auth.uid == userId in path
          if (!user?.uid) {
            throw new Error('User not authenticated');
          }
          finalImageUrl = await downloadAndUploadImage(recipe.image, recipe.title, user.uid);
          console.log('✅ Image uploaded:', finalImageUrl);
        } catch (imageError) {
          console.error('⚠️ Failed to upload image, using original URL:', imageError);
          // Continue with original URL if upload fails
        } finally {
          setIsUploadingImage(false);
        }
      }

      const matchedIngredients = recipe.ingredients.filter((ing: TikTokIngredient) => ing.standardIngredientId);
      
      if (matchedIngredients.length === 0) {
        setError('Aucun ingrédient n\'a été identifié. Veuillez matcher au moins un ingrédient.');
        setIsSaving(false);
        return;
      }

      const ingredientsWithCategories = await Promise.all(
        matchedIngredients.map(async (ing: TikTokIngredient, index: number) => {
          try {
            const standardIngDoc = await getDoc(doc(db, 'standardIngredients', ing.standardIngredientId!));
            const standardIng = standardIngDoc.data() as StandardIngredient;
            const standardUnit = mapToStandardUnit(ing.unit || 'piece');
            
            return {
              id: `${selectedOrgId}-${Date.now()}-${index}`,
              standardIngredientId: ing.standardIngredientId!,
              // Persist canonical standard ingredient name when available.
              name: standardIng?.name || ing.matchedStandardName || ing.name,
              quantity: ing.amount || 0,
              unit: standardUnit,
              category: standardIng?.category || 'other'
            };
          } catch (error) {
            console.error(`Error fetching standard ingredient ${ing.standardIngredientId}:`, error);
            return {
              id: `${selectedOrgId}-${Date.now()}-${index}`,
              standardIngredientId: ing.standardIngredientId!,
              name: ing.matchedStandardName || ing.name,
              quantity: ing.amount || 0,
              unit: mapToStandardUnit(ing.unit || 'piece'),
              category: 'other'
            };
          }
        })
      );

      const removeUndefined = (obj: any): any => {
        return Object.entries(obj).reduce((acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {} as any);
      };
      
      const instructionsString = Array.isArray(recipe.instructions) 
        ? recipe.instructions.join('\n') 
        : recipe.instructions || '';
      
      const recipeData = removeUndefined({
        name: recipe.title,
        description: recipe.description || '',
        ingredients: ingredientsWithCategories,
        instructions: instructionsString,
        prepTime: recipe.prepTime || 0,
        cookTime: recipe.cookTime || 0,
        servings: recipe.servings || 4,
        difficulty: (recipe.difficulty?.toLowerCase() || 'medium') as 'easy' | 'medium' | 'hard',
        tags: recipe.tags || [],
        image: finalImageUrl,
        sourceUrl: recipe.sourceUrl || '',
        isPublic: isPublic,
        classification: removeUndefined({
          dishTypes: recipe.classification?.dishTypes || [],
          dietaryRestrictions: recipe.classification?.dietaryRestrictions || [],
          difficulty: (recipe.classification?.difficulty || recipe.difficulty?.toLowerCase() || 'medium') as 'easy' | 'medium' | 'hard',
          cost: recipe.classification?.cost || 'medium'
        }),
        showcase: false,
        organizationId: selectedOrgId,
        createdBy: (() => {
          const selectedMember = orgMembers.find(m => m.userId === selectedUserId);
          if (selectedMember) {
            return { id: selectedMember.userId, name: selectedMember.name };
          }
          return { id: user.uid, name: user.displayName || user.email || 'Admin Menioo' };
        })(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        bookmarkCount: 0
      });

      const docRef = await addDoc(collection(db, 'recipes'), recipeData);

      if (isPublic) {
        const { updateDoc, doc } = await import('firebase/firestore');
        const seoUrl = generateRecipeSeoUrl(recipeData.name, docRef.id);
        await updateDoc(doc(db, 'recipes', docRef.id), { seoUrl });
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving recipe:', error);
      setError('Erreur lors de la sauvegarde de la recette');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const matchedCount = recipe.ingredients.filter(ing => ing.standardIngredientId).length;
  const totalCount = recipe.ingredients.length;

  return (
    <div className="space-y-6">
      {/* Recipe Metadata - Editable */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">📝 Informations de la recette</h3>
          <button
            onClick={() => setIsEditingMetadata(!isEditingMetadata)}
            className="flex items-center gap-2 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
          >
            <Edit2 size={16} />
            {isEditingMetadata ? 'Annuler' : 'Modifier'}
          </button>
        </div>

        {isEditingMetadata ? (
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
              <input
                type="text"
                value={recipe.title}
                onChange={(e) => setRecipe({ ...recipe, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={recipe.description || ''}
                onChange={(e) => setRecipe({ ...recipe, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Times and Servings */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">👥 Portions</label>
                <input
                  type="number"
                  value={recipe.servings}
                  onChange={(e) => setRecipe({ ...recipe, servings: parseInt(e.target.value) || 1 })}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">⏱️ Prep (min)</label>
                <input
                  type="number"
                  value={recipe.prepTime}
                  onChange={(e) => setRecipe({ ...recipe, prepTime: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">🔥 Cuisson (min)</label>
                <input
                  type="number"
                  value={recipe.cookTime}
                  onChange={(e) => setRecipe({ ...recipe, cookTime: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">📊 Difficulté</label>
                <select
                  value={recipe.difficulty || 'Medium'}
                  onChange={(e) => setRecipe({ ...recipe, difficulty: e.target.value as 'Easy' | 'Medium' | 'Hard' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Easy">Facile</option>
                  <option value="Medium">Moyen</option>
                  <option value="Hard">Difficile</option>
                </select>
              </div>
            </div>

            <button
              onClick={() => setIsEditingMetadata(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              ✓ Valider les modifications
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <h3 className="text-xl font-bold text-gray-900">{recipe.title}</h3>
              {recipe.description && (
                <p className="text-gray-600 mt-2">{recipe.description}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600 pt-2 border-t">
              <span>👥 {recipe.servings} pers.</span>
              <span>⏱️ Prep: {recipe.prepTime} min</span>
              <span>🔥 Cuisson: {recipe.cookTime} min</span>
              <span>⏰ Total: {recipe.prepTime + recipe.cookTime} min</span>
              <span>📊 {recipe.difficulty === 'Easy' ? 'Facile' : recipe.difficulty === 'Hard' ? 'Difficile' : 'Moyen'}</span>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Ingredients Section */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">🛒 Ingrédients</h3>
          <span className="text-sm text-gray-600">
            {matchedCount}/{totalCount} identifiés
          </span>
        </div>
        <ul className="space-y-2">
          {recipe.ingredients.map((ingredient: TikTokIngredient) => (
            <li key={ingredient.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
              {ingredient.standardIngredientId ? (
                <>
                  <span className="text-green-600">✓</span>
                  {editingIngredientId === ingredient.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="number"
                        value={editAmount}
                        onChange={(e) => setEditAmount(parseFloat(e.target.value))}
                        className="w-20 px-2 py-1 border rounded"
                        step="0.1"
                      />
                      <span>{getUnitLabel(ingredient.unit)}</span>
                      <span className="flex-1">{ingredient.name}</span>
                      <button
                        onClick={() => handleSaveIngredientEdit(ingredient.id)}
                        className="p-1 text-green-600 hover:text-green-700"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1 text-gray-600 hover:text-gray-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="font-medium">
                        {ingredient.amount > 0 ? `${ingredient.amount} ${getUnitLabel(ingredient.unit)}` : ''}
                      </span>
                      <span className="flex-1">{ingredient.name}</span>
                      <button
                        onClick={() => handleEditIngredient(ingredient.id, ingredient.amount)}
                        className="p-1 text-blue-600 hover:text-blue-700"
                        title="Modifier la quantité"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleRemoveIngredient(ingredient.id)}
                        className="p-1 text-red-600 hover:text-red-700"
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <span className="text-yellow-600">⚠️</span>
                  <span className="flex-1 text-gray-500">
                    {ingredient.amount > 0 ? `${ingredient.amount} ${ingredient.unit}` : ''} {ingredient.name}
                    <span className="text-xs ml-2">(non identifié)</span>
                  </span>
                  <button
                    onClick={() => handleRemoveIngredient(ingredient.id)}
                    className="p-1 text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Instructions Section - Editable */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">👨‍🍳 Étapes de préparation</h3>
          <button
            onClick={() => setIsEditingInstructions(!isEditingInstructions)}
            className="flex items-center gap-2 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
          >
            <Edit2 size={16} />
            {isEditingInstructions ? 'Annuler' : 'Modifier'}
          </button>
        </div>

        {isEditingInstructions ? (
          <div className="space-y-3">
            {recipe.instructions.map((step: string, index: number) => (
              <div key={index} className="flex gap-2">
                <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-700 font-semibold rounded-full mt-2">
                  {index + 1}
                </span>
                <textarea
                  value={step}
                  onChange={(e) => {
                    const newInstructions = [...recipe.instructions];
                    newInstructions[index] = e.target.value;
                    setRecipe({ ...recipe, instructions: newInstructions });
                  }}
                  rows={3}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={() => {
                    const newInstructions = recipe.instructions.filter((_, i) => i !== index);
                    setRecipe({ ...recipe, instructions: newInstructions });
                  }}
                  className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors h-fit mt-2"
                  title="Supprimer cette étape"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                setRecipe({ ...recipe, instructions: [...recipe.instructions, ''] });
              }}
              className="w-full px-4 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-500 hover:text-blue-600 transition-colors"
            >
              + Ajouter une étape
            </button>
            <button
              onClick={() => setIsEditingInstructions(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              ✓ Valider les modifications
            </button>
          </div>
        ) : (
          <ol className="space-y-3">
            {recipe.instructions.map((step: string, index: number) => (
              <li key={index} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-700 font-semibold rounded-full">
                  {index + 1}
                </span>
                <p className="flex-1 text-gray-700 pt-1">{step}</p>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Organization Selector */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Attribution</h3>
        
        <div className="space-y-4">
          {/* Organization */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="inline h-4 w-4 mr-1" />
              Organisation <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Sélectionner une organisation</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name} ({org.members.length} membres)
                </option>
              ))}
            </select>
          </div>

          {/* User */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="inline h-4 w-4 mr-1" />
              Créateur de la recette <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              required
              disabled={!selectedOrgId}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Sélectionner un membre</option>
              {user && (
                <option value={user.uid}>
                  🔧 Moi (Admin) - {user.displayName || user.email}
                </option>
              )}
              {orgMembers.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.name} - {member.role}
                </option>
              ))}
            </select>
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visibilité
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Rendre cette recette publique (visible par tous)
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t sticky bottom-0 bg-white py-4">
        <button
          onClick={onCancel}
          disabled={isSaving}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <X className="inline h-5 w-5 mr-2" />
          Annuler
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving || isUploadingImage || !selectedOrgId || !selectedUserId || matchedCount === 0}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isUploadingImage ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Téléchargement image...
            </>
          ) : isSaving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Sauvegarde...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Sauvegarder la recette
            </>
          )}
        </button>
      </div>
    </div>
  );
};

